"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useTranslations } from "next-intl"
import { Link } from "@/i18n/navigation"
import { Check, Mail, User } from "lucide-react"
import { toast } from "sonner"

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void
  }
}

export function WaitlistForm() {
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [privacyConsent, setPrivacyConsent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const t = useTranslations("waitlist.form")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch("/api/waitlist/join", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          email, 
          firstName: firstName || undefined,
          lastName: lastName || undefined,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || "Failed to join waitlist")
      }

      // Track conversion for Google Ads
      // Only fires if user has consented (handled by Consent Mode v2)
      const conversionId = process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID // e.g., AW-XXXXXXXXX/YOUR_CONVERSION_LABEL
      
      if (conversionId && typeof window !== "undefined") {
        // Wait a bit to ensure gtag is loaded, then fire conversion
        const fireConversion = () => {
          if (window.gtag) {
            window.gtag('event', 'conversion', {
              'send_to': conversionId,
              'value': 1.0,
              'currency': 'EUR',
              'transaction_id': `waitlist-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` // Unique ID to prevent duplicates
            });
            console.log('✅ Conversion event fired:', conversionId);
          } else {
            console.warn('⚠️ gtag not available yet, retrying...');
            setTimeout(fireConversion, 100);
          }
        };
        
        // Try immediately, then retry if needed
        fireConversion();
      } else {
        console.warn('⚠️ Conversion ID not configured:', conversionId);
      }

      setIsSuccess(true)
      setEmail("")
      setFirstName("")
      setLastName("")
      setPrivacyConsent(false)
      toast.success(t("success"))

      // Reset success message after 5 seconds (optional)
      // setTimeout(() => setIsSuccess(false), 5000)
    } catch (error) {
      console.error("Waitlist join error:", error)
      toast.error(
        error instanceof Error ? error.message : t("error") || "Failed to join waitlist. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <Card className="border-accent/30 bg-accent/5">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-xl bg-linear-to-br from-accent to-accent-secondary flex items-center justify-center shadow-accent">
              <Check className="h-5 w-5 text-accent-foreground" />
            </div>
            <CardTitle className="text-foreground">{t("success")}</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground">
            {t("successMessage")}
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-2xl font-semibold">Join the Waitlist</CardTitle>
        <CardDescription className="text-muted-foreground">Get early access to ClubPilot</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-foreground flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              {t("email")}
            </label>
            <Input
              id="email"
              type="email"
              placeholder={t("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              className="h-12"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="firstName" className="text-sm font-medium text-foreground flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                {t("firstName")}
              </label>
              <Input
                id="firstName"
                type="text"
                placeholder={t("firstName")}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSubmitting}
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="lastName" className="text-sm font-medium text-foreground flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                {t("lastName")}
              </label>
              <Input
                id="lastName"
                type="text"
                placeholder={t("lastName")}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSubmitting}
                className="h-12"
              />
            </div>
          </div>
          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="privacy-consent"
              checked={privacyConsent}
              onCheckedChange={(checked) => setPrivacyConsent(checked === true)}
              required
              disabled={isSubmitting}
              className="mt-0.5"
            />
            <label
              htmlFor="privacy-consent"
              className="text-sm text-foreground leading-relaxed cursor-pointer"
            >
              <span>{t("privacyConsentPrefix")}</span>{" "}
              <Link
                href="/privacy"
                className="text-accent hover:text-accent/90 underline underline-offset-4 font-medium"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {t("privacyLinkText")}
              </Link>
              <span> {t("privacyConsentSuffix")}</span>
            </label>
          </div>
          <Button
            type="submit"
            disabled={isSubmitting || !privacyConsent}
            className="w-full h-12"
            size="lg"
          >
            {isSubmitting ? t("submitting") : t("submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

